import { useCallback, useState } from 'react';

export interface GeoOrigin {
  lat: number;
  lng: number;
}

export type GeoStatus = 'idle' | 'requesting' | 'active' | 'error';

export interface UseGeolocation {
  origin: GeoOrigin | null;
  status: GeoStatus;
  error: string | null;
  request: () => void;
  clear: () => void;
}

function messageFor(err: { code: number; message: string } | null): string {
  if (err === null) return 'Unable to access your location.';
  if (err.code === 1) return 'Location access denied. Allow it in your browser to use Nearby.';
  if (err.code === 2) return 'Your location is unavailable right now. Try again later.';
  if (err.code === 3) return 'Locating you took too long. Try again.';
  return err.message || 'Unable to access your location.';
}

export function useGeolocation(): UseGeolocation {
  const [origin, setOrigin] = useState<GeoOrigin | null>(null);
  const [status, setStatus] = useState<GeoStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const request = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('error');
      setError('Geolocation is not supported by this browser.');
      setOrigin(null);
      return;
    }
    setStatus('requesting');
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setOrigin({ lat: position.coords.latitude, lng: position.coords.longitude });
        setStatus('active');
        setError(null);
      },
      (err) => {
        setStatus('error');
        setError(messageFor(err));
        setOrigin(null);
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 5 * 60_000 },
    );
  }, []);

  const clear = useCallback(() => {
    setOrigin(null);
    setStatus('idle');
    setError(null);
  }, []);

  return { origin, status, error, request, clear };
}
